import { X, Loader2 } from "lucide-react";

export function StatCard({ label, value, sub, icon: Icon, accent, testid }) {
  return (
    <div className="border border-border bg-card p-6" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
          <p className="mt-2 font-display text-3xl font-bold tracking-tight">{value}</p>
          {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
        </div>
        {Icon && (
          <div className={`flex h-10 w-10 items-center justify-center ${accent || "bg-secondary text-primary"}`}>
            <Icon size={20} />
          </div>
        )}
      </div>
    </div>
  );
}

const BADGE = {
  payée: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  paid: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  envoyée: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  brouillon: "bg-secondary text-muted-foreground border-border",
  annulée: "bg-destructive/15 text-destructive border-destructive/30",
  acceptée: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  "en attente": "bg-primary/15 text-primary border-primary/30",
  approuvé: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  refusé: "bg-destructive/15 text-destructive border-destructive/30",
  actif: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  inactif: "bg-secondary text-muted-foreground border-border",
  client: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  prospect: "bg-primary/15 text-primary border-primary/30",
  nouveau: "bg-secondary text-muted-foreground border-border",
  contacté: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  qualifié: "bg-primary/15 text-primary border-primary/30",
  proposition: "bg-purple-500/15 text-purple-400 border-purple-500/30",
  gagné: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  perdu: "bg-destructive/15 text-destructive border-destructive/30",
};

export function Badge({ children }) {
  const cls = BADGE[children] || "bg-secondary text-muted-foreground border-border";
  return (
    <span className={`inline-flex items-center border px-2 py-0.5 text-xs font-medium capitalize ${cls}`}>
      {children}
    </span>
  );
}

export function Btn({ children, variant = "primary", loading, className = "", ...props }) {
  const base =
    "inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium transition-colors duration-200 disabled:opacity-60 active:scale-[0.98]";
  const styles =
    variant === "primary"
      ? "bg-primary text-primary-foreground hover:bg-primary/90"
      : variant === "danger"
      ? "border border-destructive/40 text-destructive hover:bg-destructive/10"
      : "border border-border bg-transparent hover:bg-accent";
  return (
    <button className={`${base} ${styles} ${className}`} disabled={loading || props.disabled} {...props}>
      {loading && <Loader2 size={16} className="animate-spin" />}
      {children}
    </button>
  );
}

export function Modal({ open, onClose, title, children, wide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm">
      <div
        className={`animate-fade-up mt-10 w-full ${wide ? "max-w-3xl" : "max-w-lg"} border border-border bg-card`}
        role="dialog"
      >
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="font-display text-lg font-bold">{title}</h2>
          <button onClick={onClose} data-testid="modal-close" className="text-muted-foreground hover:text-foreground">
            <X size={20} />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto p-6">{children}</div>
      </div>
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  );
}

export const inputCls =
  "w-full border border-border bg-transparent px-3 py-2 text-sm outline-none transition-colors focus:border-primary";

export function EmptyState({ icon: Icon, title, sub, action }) {
  return (
    <div className="flex flex-col items-center justify-center border border-dashed border-border py-20 text-center">
      {Icon && <Icon size={40} className="mb-4 text-muted-foreground" />}
      <p className="font-display text-lg font-semibold">{title}</p>
      {sub && <p className="mt-1 max-w-sm text-sm text-muted-foreground">{sub}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function Toolbar({ children }) {
  return <div className="mb-6 flex flex-wrap items-center justify-between gap-4">{children}</div>;
}
